import { DiagramComponent } from '../../core/DiagramComponent';
import type { PointConfig, Variables, JXGBoard, JXGElement } from '../../types/interfaces';

// Declare global JXG for JSXGraph constants
declare const JXG: {
  COORDS_BY_USER: number;
};

interface ExtendedPointConfig extends PointConfig {
  position?: [number | string, number | string];
  movable?: boolean;
  label?: string;
  strokeColor?: string;
  attributes?: Record<string, any>;
  xVariable?: string;
  yVariable?: string;
}

interface JXGPoint extends JXGElement {
  X(): number;
  Y(): number;
  setLabel(label: string): void;
  moveTo(coords: [number, number], duration: number): void;
  coords: {
    setCoordinates(type: number, coords: [number, number]): void;
  };
}

/**
 * Point component
 * Supports both fixed and movable points via config.movable property
 */
export class Point extends DiagramComponent<ExtendedPointConfig> {
  isDragging: boolean = false;

  constructor(config: ExtendedPointConfig, board: JXGBoard, parentState: Variables = {}) {
    super(config, board, parentState);
  }

  render(): JXGPoint {
    const config = this.config;
    const position = config.position || config.coordinates || [0, 0];
    const x = this.evaluateExpression(position[0]);
    const y = this.evaluateExpression(position[1]);

    const isMovable = config.movable === true;
    
    // Evaluate label if it contains expressions
    const label = config.label || config.name ? this.evaluateExpression(config.label || config.name || '') : '';
    
    const attributes = {
      name: label,
      size: config.size || (isMovable ? 3 : 2),
      fillColor: config.color || (isMovable ? '#3498db' : '#000'),
      strokeColor: config.strokeColor || (isMovable ? '#2980b9' : '#000'),
      fixed: !isMovable,
      layer: 9, // Higher than text (8) to ensure points are always on top
      ...config.attributes,
    };

    const point = this.board.create('point', [x, y], attributes) as JXGPoint;
    
    this.jsxElements.push(point);

    // If movable, set up drag event handlers for variable updates
    if (isMovable) {
      point.on('down', () => {
        this.isDragging = true;
      });

      point.on('drag', () => {
        // Update variables directly without triggering component update
        if (config.xVariable) {
          this.localState[config.xVariable] = point.X();
          this._emitEvent('variable-change', { name: config.xVariable, value: point.X() });
        }
        if (config.yVariable) {
          this.localState[config.yVariable] = point.Y();
          this._emitEvent('variable-change', { name: config.yVariable, value: point.Y() });
        }
        
        // Update label during drag if it contains expressions
        if (config.label) {
          const updatedLabel = this.evaluateExpression(config.label);
          point.setLabel(String(updatedLabel));
        }
      });

      point.on('up', () => {
        this.isDragging = false;
        
        // Final variable update directly without triggering component update
        if (config.xVariable) {
          this.localState[config.xVariable] = point.X();
          this._emitEvent('variable-change', { name: config.xVariable, value: point.X() });
        }
        if (config.yVariable) {
          this.localState[config.yVariable] = point.Y();
          this._emitEvent('variable-change', { name: config.yVariable, value: point.Y() });
        }
        
        // Final label update
        if (config.label) {
          const updatedLabel = this.evaluateExpression(config.label);
          point.setLabel(String(updatedLabel));
          this.board.update();
        }
      });
    }

    return point;
  }

  /**
   * Update point position in place without recreating
   */
  protected _rerender(): void {
    // Don't rerender while dragging
    if (this.isDragging) {
      if (this.debug) {
        console.log(`[Point:${this.id}] Skipping rerender - currently being dragged`);
      }
      return;
    }

    if (this.jsxElements.length > 0) {
      const config = this.config;
      const position = config.position || config.coordinates || [0, 0];
      const x = Number(this.evaluateExpression(position[0]));
      const y = Number(this.evaluateExpression(position[1]));

      if (this.debug) {
        console.log(`[Point:${this.id}] Updating position to (${x}, ${y})`);
      }

      const point = this.jsxElements[0] as JXGPoint;
      if (point.moveTo) {
        point.moveTo([x, y], 0);
      } else {
        point.coords.setCoordinates(JXG.COORDS_BY_USER, [x, y]);
      }
      
      // Update label if it contains expressions
      if (config.label) {
        const updatedLabel = this.evaluateExpression(config.label);
        point.setLabel(String(updatedLabel));
      }
      
      this.board.update();
    } else {
      this.render();
    }
  }

  getSpecificity(): number {
    return 100;
  }
}

// Alias for backward compatibility
export class MovablePoint extends Point {
  constructor(config: ExtendedPointConfig, board: JXGBoard, parentState: Variables = {}) {
    super({ ...config, movable: true }, board, parentState);
  }
}
